<script lang="ts" setup>
  import { type FunctionalComponent, toRefs } from 'vue'
  import type { RouteLocationRaw } from 'vue-router'
  import { RouterLink } from 'vue-router'
  import ButtonDefault from '@/elements/ButtonDefault.vue'
  import { InternalLinkIcon } from '@/config/icons'
  import type { ButtonPassThroughOptions } from 'primevue/button'
  import type { PassThroughOptions } from 'primevue/passthrough'

  interface MergedProps {
    custom?: boolean
    /**
     * Class to apply when the link is active
     */
    activeClass?: string

    /**
     * Class to apply when the link is exact active
     */
    exactActiveClass?: string
    /**
     * Value passed to the attribute `aria-current` when the link is exact active.
     *
     * @defaultValue `'page'`
     */
    ariaCurrentValue?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true' | 'false'
    /**
     * Route Location the link should navigate to when clicked on.
     */
    to: RouteLocationRaw
    /**
     * Calls `router.replace` instead of `router.push`.
     */
    replace?: boolean
    /**
     * Name of the icon.
     */

    /**
     * Inline style of the button.
     */
    style?: never
    /**
     * Style class of the button.
     */
    class?: CssClassType
    /**
     * Text of the button.
     */
    label?: string | undefined
    /**
     * Name of the icon.
     */
    icon?: FunctionalComponent | MaterialIconVueType | false | undefined
    /**
     * Position of the icon.
     * @defaultValue left
     */
    iconPos?: 'left' | 'right' | 'top' | 'bottom' | undefined
    /**
     * Style class of the icon.
     */
    iconClass?: string | undefined
    /**
     * Value of the badge.
     */
    badge?: string | undefined
    /**
     * Style class of the badge.
     */
    badgeClass?: string | undefined
    /**
     * Severity type of the badge.
     */
    badgeSeverity?: HintedString<'secondary' | 'info' | 'success' | 'warning' | 'danger' | 'contrast'> | null | undefined
    /**
     * Whether the button is in loading state.
     * @defaultValue false
     */
    loading?: boolean | undefined
    /**
     * Icon to display in loading state.
     */
    loadingIcon?: string | undefined
    /**
     *  Add a link style to the button.
     * @defaultValue false
     */
    link?: boolean | undefined
    /**
     * Defines the style of the button.
     */
    severity?: HintedString<'secondary' | 'success' | 'info' | 'warning' | 'help' | 'danger' | 'contrast'> | undefined
    /**
     * Add a shadow to indicate elevation.
     * @defaultValue false
     */
    raised?: boolean | undefined
    /**
     * Add a circular border radius to the button.
     * @defaultValue false
     */
    rounded?: boolean | undefined
    /**
     * Add a textual class to the button without a background initially.
     * @defaultValue false
     */
    text?: boolean | undefined
    /**
     * Add a border class without a background initially.
     * @defaultValue false
     */
    outlined?: boolean | undefined
    /**
     * Defines the size of the button.
     */
    size?: 'small' | 'large' | 'extra-small' | undefined
    /**
     * Add a plain textual class to the button without a background initially.
     * @defaultValue false
     */
    plain?: boolean | undefined
    /**
     * Used to pass attributes to DOM elements inside the component.
     * @type {ButtonPassThroughOptions}
     */
    pt?: PassThrough<ButtonPassThroughOptions>
    /**
     * Used to configure passthrough(pt) options of the component.
     * @type {PassThroughOptions}
     */
    ptOptions?: PassThroughOptions
    /**
     * When enabled, it removes component related styles in the core.
     * @defaultValue false
     */
    unstyled?: boolean
  }

  const props = withDefaults(defineProps<MergedProps>(), { iconPos: undefined, icon: markRaw(InternalLinkIcon) })
  /*const {
    custom,
    activeClass,
    exactActiveClass,
    ariaCurrentValue,
    to,
    replace,
    style,
    class: cssClass,
    label,
    icon,
    iconPos,
    iconClass,
    badge,
    badgeClass,
    badgeSeverity,
    loading,
    loadingIcon,
    link,
    severity,
    raised,
    rounded,
    text,
    outlined,
    size,
    plain,
    pt,
    ptOptions,
    unstyled
  } = toRefs<MergedProps>(props)*/
  const { class: cssClass } = toRefs<MergedProps>(props)
</script>
<template>
  <RouterLink
    :active-class="activeClass"
    :aria-current-value="ariaCurrentValue"
    :class="cssClass"
    :custom="custom"
    :exact-active-class="exactActiveClass"
    :replace="replace"
    :to="to">
    <ButtonDefault
      :badge="badge"
      :badge-class="badgeClass"
      :badge-severity="badgeSeverity"
      :class="cssClass"
      :icon="icon"
      :icon-class="iconClass"
      :icon-pos="iconPos"
      :label="label"
      :link="link"
      :loading="loading"
      :loading-icon="loadingIcon"
      :outlined="outlined"
      :plain="plain"
      :pt="pt"
      :pt-options="ptOptions"
      :raised="raised"
      :rounded="rounded"
      :severity="severity"
      :size="size"
      :style="style"
      :text="text"
      :unstyled="unstyled">
      <template
        v-if="$slots.default"
        #default>
        <span class="duration-200 font-semibold">
          <slot />
        </span>
      </template>
    </ButtonDefault>
  </RouterLink>
</template>
